import { useCallback, useMemo, useReducer, useRef } from 'react'
import { isRetryableRequestFailure, retryRequestUntilSuccess, useAPI } from 'utils/API'
import { FileAPIEntry, FileAction, FileDeleteFnc, FileState, LoadUrlResolver, ReUploadOptions, SignedURLRetrieverFnc, UploadOptions, UploadUrlResolver } from './fileAPI.types'
import { AddEntryAction, FileAPIInventory, InitScopeAction, PurgeScopeAction, RemoveEntryAction, UpdateEntryAction, reduceFileAPIInventory } from './fileInventoryUtils'
import { rejectEmptyFile, startLoadWorker, uploadToGC, uploadToGCResumable } from './fileUtils'
import { getUploadConcurrency, runInLoadPool } from './uploadPool'
import { releaseLease } from './uploadLeases'

import axios from 'axios'
import constate from 'constate'
import { uniqueId } from 'lodash'
import { RecursivePartial } from 'models/helpers'
import { UrlDTO } from 'models/visuals'
import { logger } from 'utils/logging'

// The mint answers 503 until the thumbnail exists, so this is the processing wait: ~50 s covers process_image_2023_raw's slowest prod runs (~45 s, 2026-09)
const LOAD_URL_RETRY_DELAY_MS = 2000
const LOAD_URL_RETRY_LIMIT = 25

/** What the CT sees on a tile whose visual never loaded — the detail goes to Sentry instead */
const VISUAL_LOAD_FAILED_ERROR = 'Visual not found'

function* getOrderGenerator() {
  let index = 0
  while (true) {
    yield index++
  }
}

export const [FileAPIProvider, useFileAPIController] = constate(() => {
  const [fileInventory, dispatch] = useReducer(reduceFileAPIInventory, {})

  const inventoryRef = useRef<FileAPIInventory>({})
  inventoryRef.current = fileInventory

  const getNewIndex = useMemo(() => getOrderGenerator(), [])

  const createFileAPIEntry = useCallback(<MetadataType extends { [key: string]: any }>(overrides?: Partial<FileAPIEntry<MetadataType>>): FileAPIEntry<MetadataType> => {
    return {
      id: uniqueId('file-entry:'),
      state: FileState.IDLE,
      action: FileAction.INIT,
      progress: 0,
      fileObject: new File([], ''),
      abortController: new AbortController(),
      signedUrl: null,
      displayUrl: null,
      originalFilename: null,
      gcFilename: null,
      tag: null,
      error: null,
      order: getNewIndex.next().value as number,
      metadata: {} as MetadataType,
      ...overrides,
    }
  }, [getNewIndex])

  const api = useAPI<string>()

  /** Updated file entry with partially updated data */
  const updateFileEntry = <MetadataType extends { [key: string]: any }>(scope: string, id: string, fileUpdates: RecursivePartial<FileAPIEntry<MetadataType>>) => {
    dispatch(new UpdateEntryAction(scope, id, fileUpdates))
  }

  /** Adds file entry into scope */
  const addFileEntry = <MetadataType extends { [key: string]: any }>(scope: string, id: string, file: FileAPIEntry<MetadataType>, duplicityPolicy: 'skip' | 'overwrite' = 'skip') => {
    dispatch(new AddEntryAction(scope, id, file, duplicityPolicy))
  }

  /** Removes file entry from scope */
  const removeFileEntry = (scope: string, id: string) => {
    dispatch(new RemoveEntryAction(scope, id))
  }

  /** Creates new empty scope in inventory */
  const initScope = (scope: string) => {
    dispatch(new InitScopeAction(scope))
  }

  /** Removes scope from inventory, aborting whatever is still in flight in it */
  const purgeScope = (scope: string) => {
    for (const entry of Object.values(inventoryRef.current[scope] ?? {})) {
      if (entry.state === FileState.RUNNING) entry.abortController?.abort()
    }
    dispatch(new PurgeScopeAction(scope))
  }

  /** Adds all provided files to scope - mostly utility now, subject to change or be privatized */
  const initFiles = <MetadataType extends { [key: string]: any }>(scope: string, files: Array<FileAPIEntry<MetadataType>>) => {
    const addedEntries: Record<string, FileAPIEntry<MetadataType>> = {}

    for (const file of files) {

      dispatch(new AddEntryAction(
        scope,
        file.id,
        file,
        'overwrite'
      ))

      addedEntries[file.id] = file
    }

    return addedEntries
  }

  /** Calls delete fnc handler for each file and removes entry from scope if successful */
  async function deleteFiles<MetadataType extends { [key: string]: any }>(scope: string, ids: string[], deleteFnc?: FileDeleteFnc<MetadataType>, localOnly = false) {
    if (!deleteFnc && !localOnly) return

    const deletions: Promise<unknown>[] = []

    for (const id of ids) {
      let fileEntry = inventoryRef.current[scope]?.[id] as FileAPIEntry<MetadataType> | undefined
      if (!fileEntry) continue

      if (localOnly) {
        if (fileEntry.state === FileState.RUNNING) fileEntry.abortController?.abort()
        removeFileEntry(scope, id)
      } else {
        if (fileEntry.state === FileState.RUNNING) fileEntry.abortController?.abort()

        const { action: actionBeforeDelete, state: stateBeforeDelete, progress: progressBeforeDelete } = fileEntry

        updateFileEntry(scope, id, { state: FileState.RUNNING, action: FileAction.DELETE, progress: 0 })

        const deletion = deleteFnc?.(fileEntry, api)
          .then(() => {
            updateFileEntry(scope, id, { state: FileState.SUCCESS, action: FileAction.DELETE, progress: 100 })
            removeFileEntry(scope, id)
          })
          .catch((error) => {
            // The delete failed, so the blob is still there. Leaving the entry on DELETE would leave it
            // undelivered for the Submit gate with no UI path back — retry and replace are bound to the
            // upload and load actions, so the tile has to return to what it was before the attempt.
            logger.error('File delete failed', error, {
              context: { component: 'FileAPI', action: 'deleteFiles', metadata: { scope, fileId: id } },
            })

            // An upload we aborted on the way in is not running any more, so it settles as a failed
            // upload the creative can retry; anything else goes back to the state it was delivered in.
            updateFileEntry(scope, id, {
              state: stateBeforeDelete === FileState.RUNNING ? FileState.ERROR : stateBeforeDelete,
              action: actionBeforeDelete,
              progress: stateBeforeDelete === FileState.RUNNING ? 0 : progressBeforeDelete,
              error: error instanceof Error ? error.message : String(error),
            })
          })

        if (deletion) deletions.push(deletion)
      }
    }

    await Promise.all(deletions)
  }

  /** Obtains signedUrl for each file and proceeds with parallel upload of all files updating progress */
  async function uploadFiles<MetadataType extends { [key: string]: any }>(
    scope: string,
    files: FileList | Array<File>,
    signedUrlResolver: SignedURLRetrieverFnc<MetadataType>,
    options: UploadOptions<MetadataType>
  ) {

    // Should be separated probably
    const initializedFiles = initFiles<MetadataType>(
      scope,
      Array.from(files).map((file) => createFileAPIEntry<MetadataType>({
        fileObject: file,
        tag: options.tag,
        ...(options.metadata && { metadata: { ...options.metadata } as MetadataType }),
        originalFilename: file.name,
        state: FileState.RUNNING,
        action: FileAction.UPLOAD,
        progress: 0,
      }))
    )

    // Reject empty (0-byte) files before minting signed URLs, so no backend record /
    // GCS object is created for a file that can never upload successfully (ISSUE-2042)
    const emptyFileIds: string[] = []

    for (const file of Object.values(initializedFiles)) {
      rejectEmptyFile(file.fileObject, 'upload', (error?: string) => {
        updateFileEntry(scope, file.id, { state: FileState.ERROR, progress: 0, error: error ?? null })
        emptyFileIds.push(file.id)
        delete initializedFiles[file.id]
      })
    }

    if (Object.keys(initializedFiles).length === 0) {
      options.onSettled?.([], emptyFileIds, [])
      return
    }

    const pending = Object.values(initializedFiles)

    const allUploads: Promise<unknown>[] = []
    const successIds: string[] = []
    const cancelledIds: string[] = []
    const errorIds: string[] = []

    const startUpload = (file: FileAPIEntry<MetadataType>, onStart: () => void) => {
      const onProgress = (progress: number) => {
        updateFileEntry(scope, file.id, { progress })
      }
      const onSuccess = () => {
        updateFileEntry(scope, file.id, { state: FileState.SUCCESS, progress: 100 })
        successIds.push(file.id)
      }
      const onCancel = () => {
        updateFileEntry(scope, file.id, { state: FileState.CANCELLED, progress: 0 })
        cancelledIds.push(file.id)
      }
      const onError = (error?: string) => {
        updateFileEntry(scope, file.id, { state: FileState.ERROR, progress: 0, error: error ?? null })
        errorIds.push(file.id)
      }

      return file.signedUrl!.headers['x-goog-resumable']
        ? uploadToGCResumable(file.fileObject, file.signedUrl!, file.abortController!.signal, onProgress, onSuccess, onCancel, onError, undefined, undefined, onStart)
        : uploadToGC(file.fileObject, file.signedUrl!, file.abortController!.signal, onProgress, onSuccess, onCancel, onError, undefined, onStart)
    }

    const settleAsError = (entries: Array<FileAPIEntry<MetadataType>>) => {
      for (const file of entries) {
        updateFileEntry(scope, file.id, { state: FileState.ERROR, progress: 0, error: 'Signed URL retrieval failed' })
        errorIds.push(file.id)
      }
    }

    let start = 0

    while (start < pending.length) {
      // Wave = pool width, so a URL is at most one wave old when its file takes a slot and never
      // waits in the queue longer than that (a re-mint would reserve a new blob name and orphan this one).
      // Re-read per wave: the LaunchDarkly `upload-concurrency` flag can move the pool bound
      // mid-drop, and a wave wider than the pool reopens exactly that aging window.
      const slice = pending.slice(start, start + Math.max(1, getUploadConcurrency()))
      // A file removed while it waited for its wave is not minted at all.
      const wave = slice.filter((file) => !file.abortController?.signal.aborted)
      if (!wave.length) {
        start += slice.length
        continue
      }

      let minted: Awaited<ReturnType<SignedURLRetrieverFnc<MetadataType>>>

      try {
        minted = await signedUrlResolver(wave, api)
      } catch (error) {
        // No caller awaits uploadFiles, so an escaping rejection would only ever surface as a
        // context-free unhandledrejection, with every remaining file stuck RUNNING and no onSettled.
        logger.error('Upload wave mint threw, abandoning remaining files', error, {
          context: {
            component: 'FileAPIController',
            action: 'uploadFiles',
            metadata: { scope, waveSize: wave.length, abandonedCount: pending.length - start },
          },
        })
        settleAsError(pending.slice(start))
        break
      }

      const { successfulCalls, cancelledCalls, errorCalls } = minted
      const discarded: FileAPIEntry<MetadataType>[] = []

      for (const file of wave) {
        const data = successfulCalls[file.id]

        if (file.abortController?.signal.aborted) {
          if (data) {
            discarded.push({ ...file, signedUrl: data, gcFilename: data.filename })
            void releaseLease(data.leaseId)
          }
          continue
        }

        if (!data) {
          if (cancelledCalls.includes(file.id)) {
            updateFileEntry(scope, file.id, { state: FileState.CANCELLED, progress: 0 })
            cancelledIds.push(file.id)
          } else {
            // An explicit error and a 200 that omitted this filename land here alike; a silent
            // skip would leave the entry RUNNING at 0% forever. Only the omission needs a log —
            // the resolvers report a request that threw themselves.
            if (!errorCalls.includes(file.id)) {
              logger.error('Wave mint returned no signed URL for file', null, {
                context: {
                  component: 'FileAPIController',
                  action: 'uploadFiles',
                  metadata: { scope, fileName: file.fileObject?.name, tag: options.tag, mintedCount: Object.keys(successfulCalls).length, waveSize: wave.length },
                },
              })
            }

            updateFileEntry(scope, file.id, { state: FileState.ERROR, progress: 0, error: 'Signed URL retrieval failed' })
            errorIds.push(file.id)
          }
          continue
        }

        file.signedUrl = data
        file.gcFilename = data.filename

        // Either use passed id creator fnc to generate new id, or default to gcFilename
        const newFileId = options.fileIdCreatorFnc
          ? options.fileIdCreatorFnc(file)
          : file.gcFilename

        // Assign signedUrl and change temporary id to GC filename
        updateFileEntry(scope, file.id, {
          signedUrl: data,
          gcFilename: data.filename,
          id: newFileId
        })

        // Replace temporary id by newly created id based on file
        file.id = newFileId
      }

      if (discarded.length) options.onDiscardedAfterMint?.(discarded, api)

      const waveStarts = wave
        .filter((file) => {
          if (file.fileObject && file.signedUrl && file.abortController) return true

          // An entry with no signedUrl was just settled above. Anything else dropped here never
          // reaches startUpload and would sit on RUNNING at 0%, absent from every onSettled bucket.
          if (file.signedUrl) {
            logger.warn('Upload entry dropped before dispatch', null, {
              context: {
                component: 'FileAPIController',
                action: 'uploadFiles',
                metadata: { scope, fileName: file.fileObject?.name, missing: file.fileObject ? 'abortController' : 'fileObject' },
              },
            })
          }

          return false
        })
        .map((file) => new Promise<void>((started) => {
          const upload = startUpload(file, started)
          allUploads.push(upload)
          // Settling counts as started, so a file that never reached a slot cannot hold the drop
          void upload.then(started, started)
        }))
      start += slice.length

      // Only a wave the backend refused or the caller cancelled outright will repeat on the next
      // wave, so settle the remaining files the same way and stop. A 200 that merely omitted
      // files is not that — the waves behind it still get their own chance.
      const refused = cancelledCalls.length + errorCalls.length === wave.length

      if (refused) {
        const cancelled = cancelledCalls.length === wave.length
        const abandoned = pending.slice(start)

        // The resolver's own Sentry event counts only the wave, so without this the incident
        // reads as 3 failed files when the whole rest of the drop was abandoned.
        logger.error('Upload wave refused, abandoning remaining files', null, {
          context: {
            component: 'FileAPIController',
            action: 'uploadFiles',
            metadata: { scope, waveSize: wave.length, abandonedCount: abandoned.length, cancelled },
          },
        })

        if (cancelled) {
          for (const file of abandoned) {
            updateFileEntry(scope, file.id, { state: FileState.CANCELLED, progress: 0 })
            cancelledIds.push(file.id)
          }
        } else {
          settleAsError(abandoned)
        }
        break
      }

      // Mint the next wave only once every file of this one has taken a pool slot. Its URLs then
      // wait at most a pool width of completions before their files initiate while this drop is the
      // pool's sole user; each concurrent drop on the app-wide `runInUploadPool` (gallery drops, thread
      // attachments, PhotoStudio masks) adds another pool width. Gating on a wave draining instead let
      // one stalled file freeze the drop (QA-460).
      await Promise.all(waveStarts)
    }

    await Promise.allSettled(allUploads)
    options.onSettled?.(successIds, [...emptyFileIds, ...errorIds], cancelledIds)
  }

  /** Obtains signedUrl for each file and proceeds with parallel upload of all files updating progress */
  async function reUploadFile<MetadataType extends { [key: string]: any }>(
    scope: string,
    replaceId: string,
    file: File,
    signedUrlResolver: UploadUrlResolver<MetadataType>,
    options: ReUploadOptions<MetadataType>
  ) {

    const entry = fileInventory[scope]?.[replaceId] as FileAPIEntry<MetadataType> | undefined

    if (!entry) {
      console.error('No existing entry for this scope and id, nothing to replace, aborting!')
      return
    }

    // Reject an empty (0-byte) replacement before minting a signed URL (ISSUE-2042)
    const isEmptyFile = rejectEmptyFile(file, 're-upload', (error?: string) => {
      updateFileEntry(scope, entry.id, { state: FileState.ERROR, progress: 0, error: error ?? null })
      options.onError?.()
    })

    if (isEmptyFile) return

    const abortController = new AbortController()

    // Set state and action and update file object data in state
    updateFileEntry(scope, entry.id, {
      state: FileState.RUNNING,
      action: FileAction.RE_UPLOAD,
      progress: 0,
      fileObject: file,
      originalFilename: file.name,
      tag: options.tag ?? entry.tag,
      abortController,
    })

    // Update crucial details in fnc kept entry
    entry.fileObject = file
    entry.originalFilename = file.name
    entry.tag = options.tag ?? entry.tag
    entry.abortController = abortController

    // Obtain signed url
    await signedUrlResolver(entry, api)
      .then((response) => {
        const signedUrl = response.data

        // Assign signedUrl and change temporary id to GC filename
        updateFileEntry(scope, entry.id, {
          signedUrl,
          gcFilename: signedUrl.filename,
          // Should be the same, just to be sure
          id: signedUrl.filename,
        })

        // Update signed url of entry
        entry.signedUrl = signedUrl
        // Should be the same, just to be sure
        entry.id = signedUrl.filename
      })
      .catch((e) => {
        if (axios.isCancel(e)) {
          updateFileEntry(scope, entry.id, { state: FileState.CANCELLED, progress: 0 })
          options.onCancel?.()
        } else {
          updateFileEntry(scope, entry.id, { state: FileState.ERROR, progress: 0 })
          options.onError?.()
        }
      })

    // Sanity check
    if (!entry || !entry.fileObject || !entry.signedUrl || !entry.abortController) return

    if (entry.signedUrl.headers['x-goog-resumable']) {
      uploadToGCResumable(
        entry.fileObject,
        entry.signedUrl,
        entry.abortController.signal,
        (progress) => {
          updateFileEntry(scope, entry.id, { progress })
        },
        // on success
        () => {
          updateFileEntry(scope, entry.id, { state: FileState.SUCCESS, progress: 100 })
          options.onSuccess?.()
        },
        // on cancel
        () => {
          updateFileEntry(scope, entry.id, { state: FileState.CANCELLED, progress: 0 })
          options.onCancel?.()
        },
        // on error
        (error?: string) => {
          updateFileEntry(scope, entry.id, { state: FileState.ERROR, progress: 0, error: error ?? null })
          options.onError?.()
        }
      )
    } else {
      uploadToGC(
        entry.fileObject,
        entry.signedUrl,
        entry.abortController.signal,
        (progress) => {
          updateFileEntry(scope, entry.id, { progress })
        },
        // on success
        () => {
          updateFileEntry(scope, entry.id, { state: FileState.SUCCESS, progress: 100 })
          options.onSuccess?.()
        },
        // on cancel
        () => {
          updateFileEntry(scope, entry.id, { state: FileState.CANCELLED, progress: 0 })
          options.onCancel?.()
        },
        // on error
        (error?: string) => {
          updateFileEntry(scope, entry.id, { state: FileState.ERROR, progress: 0, error: error ?? null })
          options.onError?.()
        }
      )
    }
  }

  const loadFile = async <MetadataType extends { [key: string]: any }>(scope: string, fileId: string, urlResolver: LoadUrlResolver<MetadataType>) => {

    const entry = inventoryRef.current[scope]?.[fileId] as FileAPIEntry<MetadataType> | undefined

    if (!entry) {
      console.warn(`No file entry for ${fileId} found, skipping.`)
      return
    }

    updateFileEntry(scope, fileId, {
      action: FileAction.LOAD,
      state: FileState.RUNNING,
      progress: 0,
    })

    const logMetadata = { scope, fileId, gcFilename: entry.gcFilename }

    // One slot covers mint and blob fetch, so a page that hydrates a whole assignment at mount
    // (ISSUE-3455) cannot open one HTTP/2 stream per visual. Queued entries wait as RUNNING/LOAD,
    // which neither gates Submit nor the dropzone.
    await runInLoadPool(async () => {
      // A thumbnail still being generated comes back as 503, so this retry is where the page waits for
      // processing; the worker only ever gets a URL for a blob that already exists
      const urlResponse = await retryRequestUntilSuccess<UrlDTO>(
        () => urlResolver(entry, api),
        LOAD_URL_RETRY_DELAY_MS,
        LOAD_URL_RETRY_LIMIT,
        (error) => isRetryableRequestFailure(error) && !!inventoryRef.current[scope]?.[fileId]
      )

      // retryRequestUntilSuccess hands back whatever was thrown, so anything without a response body is a
      // failure — testing for AxiosError alone lets a non-axios throwable through into urlResponse.data,
      // and a thrown primitive would make the `in` operator itself throw
      if (!urlResponse || typeof urlResponse !== 'object' || axios.isAxiosError(urlResponse) || !('data' in urlResponse)) {
        logger.error('Load URL mint failed', urlResponse instanceof Error ? urlResponse : undefined, {
          context: {
            component: 'FileAPIController',
            action: 'loadFile',
            metadata: {
              ...logMetadata,
              status: axios.isAxiosError(urlResponse) ? urlResponse.response?.status : undefined,
            },
          },
        })

        updateFileEntry(scope, fileId, {
          state: FileState.ERROR,
          progress: 0,
          error: urlResponse instanceof Error ? urlResponse.message : 'Load URL could not be retrieved',
        })
        return
      }

      const signedURL = urlResponse.data.url

      try {
        await startLoadWorker(signedURL)

        updateFileEntry(scope, fileId, {
          displayUrl: signedURL,
          state: FileState.SUCCESS,
          progress: 0,
        })
      } catch (err: any) {
        // The worker exhausted its own retries, so this is the only place the failure is observable
        logger.error('Visual load failed after worker retries', err, {
          context: {
            component: 'FileAPIController',
            action: 'loadFile',
            metadata: logMetadata,
          },
        })

        // The entry's error reaches the user as the tile's message, so it stays the copy it has always been
        updateFileEntry(scope, fileId, {
          state: FileState.ERROR,
          progress: 0,
          error: VISUAL_LOAD_FAILED_ERROR,
        })
      }
    })
  }

  const loadFiles = async <MetadataType extends { [key: string]: any }>(scope: string, fileIds: Array<string>, urlResolver: LoadUrlResolver<MetadataType>) => {
    await Promise.all(fileIds.map((fileId) => loadFile(scope, fileId, urlResolver)))
  }

  return {
    uploadFiles,
    reUploadFile,
    fileInventory,
    addFileEntry,
    updateFileEntry,
    initScope,
    purgeScope,
    deleteFiles,
    initFiles,
    loadFiles,
    createFileAPIEntry,
  }
})
